import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const BOOT_LINES = [
    'Initializing Portfolio...',
    'Decrypting Assets...',
    'Loading Identity...',
    'Scanning Credentials...',
    'Establishing Secure Connection...',
    'Verifying User...',
    'Loading Interface...',
]

const SESSION_KEY = 'portfolio_boot_done'

export default function StartupScreen() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const alreadyBooted = sessionStorage.getItem(SESSION_KEY) === '1'

    const [phase, setPhase] = useState<'booting' | 'granted' | 'waiting' | 'done'>(
        alreadyBooted ? 'done' : prefersReducedMotion ? 'waiting' : 'booting'
    )
    const [lines, setLines] = useState<string[]>([])
    const [progress, setProgress] = useState(0)
    const [skipped, setSkipped] = useState(false)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    /* Lock body scroll while overlay is visible */
    useEffect(() => {
        if (phase === 'done') return
        const originalOverflow = document.body.style.overflow
        const originalPosition = document.body.style.position
        const originalWidth    = document.body.style.width
        const scrollY = window.scrollY

        document.body.style.overflow = 'hidden'
        document.body.style.position = 'fixed'
        document.body.style.top      = `-${scrollY}px`
        document.body.style.width    = '100%'

        return () => {
            document.body.style.overflow = originalOverflow
            document.body.style.position = originalPosition
            document.body.style.width    = originalWidth
            document.body.style.top      = ''
            window.scrollTo(0, scrollY)
        }
    }, [phase])

    /* Skip on any keypress / tap during booting */
    useEffect(() => {
        if (phase !== 'booting') return
        const skip = () => {
            setSkipped(true)
            setLines(BOOT_LINES)
            setProgress(100)
            setPhase('granted')
        }
        window.addEventListener('keydown', skip, { once: true })
        window.addEventListener('pointerdown', skip, { once: true })
        return () => {
            window.removeEventListener('keydown', skip)
            window.removeEventListener('pointerdown', skip)
        }
    }, [phase])

    /* Sequential line reveal + progress */
    useEffect(() => {
        if (phase !== 'booting' || skipped) return

        const total = BOOT_LINES.length
        let idx = 0

        const step = () => {
            if (idx < total) {
                setLines(prev => [...prev, BOOT_LINES[idx]])
                setProgress(Math.round(((idx + 1) / total) * 100))
                idx++
                timerRef.current = setTimeout(step, 380)
            } else {
                timerRef.current = setTimeout(() => setPhase('granted'), 300)
            }
        }

        timerRef.current = setTimeout(step, 250)
        return () => { if (timerRef.current) clearTimeout(timerRef.current) }
    }, [phase, skipped])

    /* Hold "ACCESS GRANTED" then transition to waiting */
    useEffect(() => {
        if (phase !== 'granted') return
        timerRef.current = setTimeout(() => setPhase('waiting'), 600)
        return () => { if (timerRef.current) clearTimeout(timerRef.current) }
    }, [phase])

    /* Click / tap to enter from 'waiting' phase */
    const handleEnter = () => {
        if (phase !== 'waiting') return
        sessionStorage.setItem(SESSION_KEY, '1')
        setPhase('done')
    }

    if (phase === 'done' || alreadyBooted) return null

    return (
        <AnimatePresence>
            <motion.div
                key="boot"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                onClick={handleEnter}
                role="dialog"
                aria-label="Portfolio boot sequence"
                aria-live="polite"
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 9999,
                    background: '#000000',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    /* Safe area padding for iPhone notch & home indicator */
                    paddingTop:    'max(2.5rem, env(safe-area-inset-top))',
                    paddingBottom: 'max(2rem, env(safe-area-inset-bottom))',
                    paddingLeft:   'max(1.5rem, env(safe-area-inset-left))',
                    paddingRight:  'max(1.5rem, env(safe-area-inset-right))',
                    /* Prevent any inner scroll */
                    overflow: 'hidden',
                    /* Avoid being pushed by virtual keyboard */
                    touchAction: 'none',
                    /* Use dynamic viewport height so it covers 100% on mobile Safari */
                    height: '100dvh',
                    width: '100dvw',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                }}
            >
                {/* Faint scanline texture */}
                <div
                    className="terminal-scanlines opacity-40"
                    style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}
                />

                <div
                    style={{
                        position: 'relative',
                        zIndex: 10,
                        width: '100%',
                        maxWidth: '36rem',
                        /* Let content scroll internally only if truly needed */
                        overflowY: 'auto',
                        maxHeight: '100%',
                    }}
                >
                    {/* Boot lines */}
                    <div style={{ marginBottom: '1.5rem', minHeight: '10rem' }}>
                        {lines.map((line, i) => (
                            <motion.p
                                key={i}
                                initial={{ opacity: 0, x: -8 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.2 }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    fontSize: 'clamp(0.65rem, 2.5vw, 0.75rem)',
                                    color: '#888',
                                    fontFamily: 'inherit',
                                    marginBottom: '0.25rem',
                                    lineHeight: 1.5,
                                }}
                            >
                                <span style={{ color: '#00FF88', fontSize: '0.625rem' }}>▸</span>
                                {line}
                                {i === lines.length - 1 && phase === 'booting' && (
                                    <span className="animate-enc-blink" style={{ color: '#00FF88' }}>_</span>
                                )}
                                {i < lines.length - 1 && (
                                    <span style={{ color: '#00FF88', fontSize: '0.625rem' }}>OK</span>
                                )}
                            </motion.p>
                        ))}
                    </div>

                    {/* Progress bar */}
                    {(phase === 'booting' || phase === 'granted' || phase === 'waiting') && (
                        <div style={{ marginBottom: '2rem' }}>
                            <div style={{ height: '1px', background: '#111', width: '100%', marginBottom: '0.25rem' }}>
                                <motion.div
                                    style={{ height: '100%', background: '#00FF88' }}
                                    initial={{ width: 0 }}
                                    animate={{ width: `${progress}%` }}
                                    transition={{ duration: 0.3, ease: 'linear' }}
                                />
                            </div>
                            <p style={{ fontSize: '0.625rem', color: '#444', fontFamily: 'inherit' }}>
                                {progress}%
                            </p>
                        </div>
                    )}

                    {/* ACCESS GRANTED */}
                    {(phase === 'granted' || phase === 'waiting') && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            style={{ marginBottom: '1.5rem' }}
                        >
                            <p
                                style={{
                                    fontSize: 'clamp(1.1rem, 6vw, 1.5rem)',
                                    fontWeight: 700,
                                    color: '#00FF88',
                                    letterSpacing: '0.15em',
                                    fontFamily: 'inherit',
                                }}
                            >
                                ACCESS GRANTED
                            </p>
                        </motion.div>
                    )}

                    {/* Click / tap to enter */}
                    {phase === 'waiting' && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.4 }}
                        >
                            <p
                                style={{
                                    fontSize: 'clamp(0.65rem, 2.5vw, 0.75rem)',
                                    color: '#555',
                                    fontFamily: 'inherit',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                }}
                            >
                                <span
                                    className="animate-blink"
                                    style={{
                                        display: 'inline-block',
                                        width: '0.5rem',
                                        height: '0.75rem',
                                        background: '#00FF88',
                                        flexShrink: 0,
                                    }}
                                />
                                {/* Mobile-friendly tap prompt */}
                                <span>
                                    {window.matchMedia('(pointer: coarse)').matches
                                        ? 'Tap anywhere to initialize system'
                                        : 'Click anywhere to initialize system'}
                                </span>
                            </p>
                        </motion.div>
                    )}
                </div>
            </motion.div>
        </AnimatePresence>
    )
}
